import React, { useEffect, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { artistAnalyticsService } from '../../services/api/artist-analytics.service';
import type { PlatformGrowthData } from '../../services/api/artist-analytics.service';
import {
  getCategoryAxisLayout,
  splitParentheticalCategoryLabel,
} from '../../utils/echartsCategoryAxis';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';

interface PlatformGrowthComparisonChartProps {
  artistId?: string;
  title?: string;
  className?: string;
  refreshKey?: number;
}

const PlatformGrowthComparisonChart: React.FC<PlatformGrowthComparisonChartProps> = ({
  artistId,
  title = 'Platform Growth Comparison',
  className = '',
  refreshKey = 0
}) => {
  const [data, setData] = useState<PlatformGrowthData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await artistAnalyticsService.getPlatformGrowthData(artistId);
        setData(response.data);
      } catch (err) {
        setError('Failed to load platform growth comparison data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artistId, refreshKey]);

  const getChartOption = (): EChartsOption => {
    if (!data || data.length === 0) return {};

    // Filter out platforms with no growth and sort by current value
    const platformData = data
      .filter(item => item.weekly_growth !== 0 || item.monthly_growth !== 0)
      .sort((a, b) => b.current_value - a.current_value);

    const platforms = platformData.map(item => `${item.platform} (${item.metric_type})`);
    const weeklyGrowth = platformData.map(item => item.weekly_growth);
    const monthlyGrowth = platformData.map(item => item.monthly_growth);
    const xLayout = getCategoryAxisLayout(platforms.length, 'longLabels');

    return {
      backgroundColor: '#ffffff',
      color: ['#4FC3F7', '#9C27B0'], // Set explicit colors for series and legend
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          color: '#1f2937',
          fontSize: 12
        },
        formatter: function(params: any) {
          const dataIndex = params[0].dataIndex;
          const item = platformData[dataIndex];
          
          return `
            <div style="font-weight: 600; margin-bottom: 8px; color: #fff;">${item.platform} (${item.metric_type})</div>
            <div style="color: #4FC3F7; margin-bottom: 4px;">
              ● Weekly: ${item.weekly_growth >= 0 ? '+' : ''}${item.weekly_growth.toLocaleString()} (${item.weekly_growth_rate.toFixed(1)}%)
            </div>
            <div style="color: #9C27B0; margin-bottom: 4px;">
              ● Monthly: ${item.monthly_growth >= 0 ? '+' : ''}${item.monthly_growth.toLocaleString()} (${item.monthly_growth_rate.toFixed(1)}%)
            </div>
            <div style="color: #9ca3af; font-size: 11px;">
              Current Total: ${item.current_value.toLocaleString()}
            </div>
          `;
        }
      },
      legend: {
        data: ['Weekly Growth', 'Monthly Growth'],
        textStyle: {
          color: '#6b7280',
          fontSize: 11
        },
        top: 5,
        right: 20,
        itemWidth: 14,
        itemHeight: 10,
        itemGap: 10
      },
      grid: {
        left: '10%',
        right: '10%',
        top: '20%',
        bottom: xLayout.gridBottom,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: platforms,
        axisLine: {
          lineStyle: {
            color: '#d1d5db'
          }
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: xLayout.fontSize,
          rotate: xLayout.rotate,
          interval: 0,
          lineHeight: 14,
          formatter: (value: string) => splitParentheticalCategoryLabel(value)
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: false
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: 10,
          formatter: function(value: number) {
            if (Math.abs(value) >= 1000000) {
              return `${(value / 1000000).toFixed(1)}M`;
            } else if (Math.abs(value) >= 1000) {
              return `${(value / 1000).toFixed(1)}K`;
            }
            return value.toString();
          }
        },
        splitLine: {
          lineStyle: {
            color: '#d1d5db',
            width: 1
          }
        },
        splitNumber: 4
      },
      series: [
        {
          name: 'Weekly Growth',
          type: 'bar',
          color: '#4FC3F7', // Set color at series level for legend
          data: weeklyGrowth.map((value) => ({
            value: value,
            itemStyle: {
              color: '#4FC3F7',
              borderRadius: value >= 0 ? [2, 2, 0, 0] : [0, 0, 2, 2]
            }
          })),
          barWidth: '30%',
          barGap: '10%',
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(79, 195, 247, 0.3)'
            }
          }
        },
        {
          name: 'Monthly Growth',
          type: 'bar',
          color: '#9C27B0', // Set color at series level for legend
          data: monthlyGrowth.map((value) => ({
            value: value,
            itemStyle: {
              color: '#9C27B0',
              borderRadius: value >= 0 ? [2, 2, 0, 0] : [0, 0, 2, 2]
            }
          })),
          barWidth: '30%',
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(156, 39, 176, 0.3)'
            }
          }
        }
      ]
    };
  };

  if (loading) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <p className="text-red-400 text-xs text-center px-2">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  const plottableGrowth = data.filter(
    (item) => item.weekly_growth !== 0 || item.monthly_growth !== 0
  );
  if (plottableGrowth.length === 0) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <p className="text-red-400 text-xs text-center px-3 max-w-xs">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-transparent p-2">
      <BaseChart
        option={getChartOption()}
        loading={loading}
        className="w-full h-full"
      />
    </div>
  );
};

export default PlatformGrowthComparisonChart;
